/**
 * Cross-Origin Isolation Utility
 * Manages dynamic enabling of COOP/COEP headers for Whisper support
 */

import { useState, useEffect } from 'react';

/**
 * Check if cross-origin isolation is currently enabled
 */
export const isCrossOriginIsolated = () => {
    return window.crossOriginIsolated === true;
};

/**
 * Check if COI parameter is in URL
 */
export const hasCOIParameter = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('coi') === '1';
};

/**
 * Enable cross-origin isolation by reloading page with COI parameter
 * This triggers the server to send COOP/COEP headers
 */
export const enableCrossOriginIsolation = () => {
    if (isCrossOriginIsolated()) {
        // console.log('✅ Already cross-origin isolated');
        return Promise.resolve(true);
    }

    if (hasCOIParameter()) {
        console.warn('⚠️ COI parameter present but not isolated - browser may not support it');
        return Promise.resolve(false);
    }

    // console.log('🔄 Enabling cross-origin isolation...');

    // Preserve authentication token and user data
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (token) {
        sessionStorage.setItem('coi_temp_token', token);
    }
    if (user) {
        sessionStorage.setItem('coi_temp_user', user);
    }

    // Add COI parameter and reload
    const url = new URL(window.location.href);
    url.searchParams.set('coi', '1');

    // Store current path to restore after reload
    sessionStorage.setItem('coi_restore_path', window.location.pathname);

    // Reload with COI enabled
    window.location.href = url.toString();

    // Return a promise that never resolves (page will reload)
    return new Promise(() => {});
};

/**
 * Restore authentication after COI reload
 * This should be called on app initialization
 */
export const restoreAuthAfterCOI = () => {
    const token = sessionStorage.getItem('coi_temp_token');
    const user = sessionStorage.getItem('coi_temp_user');

    if (token && user) {
        // console.log('🔄 Restoring authentication after COI reload...');
        localStorage.setItem('token', token);
        localStorage.setItem('user', user);

        // Cleanup
        sessionStorage.removeItem('coi_temp_token');
        sessionStorage.removeItem('coi_temp_user');
        sessionStorage.removeItem('coi_restore_path');

        return true;
    }

    return false;
};

/**
 * Restore path after COI reload
 */
export const restorePath = () => {
    const savedPath = sessionStorage.getItem('coi_restore_path');
    if (savedPath && savedPath !== window.location.pathname) {
        sessionStorage.removeItem('coi_restore_path');
        return savedPath;
    }
    return null;
};

/**
 * React hook to ensure cross-origin isolation
 * Automatically reloads the page with COI if needed
 */
export const useRequireCrossOriginIsolation = () => {
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const checkAndEnable = async () => {
            try {
                if (isCrossOriginIsolated()) {
                    setIsReady(true);
                    return;
                }

                // Not isolated - need to reload with COI
                await enableCrossOriginIsolation();
            } catch (err) {
                setError(err.message);
                setIsReady(false);
            }
        };

        checkAndEnable();
    }, []);

    return { isReady, error, isCrossOriginIsolated: isCrossOriginIsolated() };
};

// For non-React usage
const crossOriginIsolation = {
    isCrossOriginIsolated,
    hasCOIParameter,
    enableCrossOriginIsolation,
    restorePath
};

export default crossOriginIsolation;
